import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useLang } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { API } from "../api";
import {
  Users, CalendarClock, Wallet, ReceiptText, Clock, Umbrella,
  Bot, ShieldCheck, Building2, ArrowRight, CheckCircle2,
} from "lucide-react";

export function googleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const MODULES = [
  { icon: Users, en: "Employee Management", bm: "Pengurusan Pekerja" },
  { icon: Clock, en: "Attendance & GPS Clock-In", bm: "Kehadiran & Clock-In GPS" },
  { icon: Umbrella, en: "Leave Management", bm: "Pengurusan Cuti" },
  { icon: Wallet, en: "Malaysian Payroll (EPF/SOCSO/EIS/PCB)", bm: "Payroll Malaysia (EPF/SOCSO/EIS/PCB)" },
  { icon: ReceiptText, en: "Claims & Expenses", bm: "Tuntutan & Perbelanjaan" },
  { icon: CalendarClock, en: "Approval Workflows", bm: "Aliran Kerja Kelulusan" },
  { icon: Bot, en: "AI HR Assistant (BM/EN)", bm: "Pembantu HR AI (BM/EN)" },
  { icon: Building2, en: "White Label & Multi-Tenant", bm: "White Label & Multi-Tenant" },
];

const INDUSTRIES = ["SME", "Retail", "F&B", "Construction", "Manufacturing", "Logistics", "Hospitality", "Education", "Professional Services", "Franchise"];

export default function Landing() {
  const { lang, setLang, t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    fetch(`${API}/plans`).then((r) => r.json()).then((d) => setPlans(d.plans || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  const bm = lang === "bm";

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="https://static.prod-images.emergentagent.com/jobs/38410d7b-1765-4bbf-bba1-c88346c63df2/images/17cf024fd8bae2189f335f9e97620d8dedc57e9bfa1f936981ac8140ad193242.jpeg" alt="WORKAHOLIC HR Solution" className="w-9 h-9 rounded-lg object-cover" />
            <div>
              <span className="font-display font-bold text-lg tracking-tight">WORKAHOLIC</span>
              <span className="hidden sm:inline text-[10px] font-semibold text-slate-500 ml-1.5 uppercase tracking-wider">HR Solution</span>
            </div>
            <Badge variant="outline" className="ml-1 text-xs">Malaysia</Badge>
          </div>
          <div className="flex items-center gap-2">
            <div data-testid="lang-toggle-bm-en" className="flex items-center border rounded-full p-0.5 bg-slate-50">
              {["bm", "en"].map((l) => (
                <button key={l} data-testid={`lang-option-${l}`} onClick={() => setLang(l)}
                  className={`px-3 py-1 rounded-full text-xs font-semibold uppercase transition-colors ${lang === l ? "bg-blue-800 text-white" : "text-slate-500 hover:text-slate-800"}`}>
                  {l}
                </button>
              ))}
            </div>
            <Button data-testid="google-login-button" size="sm" className="bg-blue-800 hover:bg-blue-900 whitespace-nowrap" onClick={googleLogin}>
              <span className="hidden xs:inline sm:inline">{t("landing.login")}</span>
              <span className="sm:hidden">{bm ? "Log Masuk" : "Sign In"}</span>
            </Button>
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <Badge className="bg-blue-50 text-blue-800 border-blue-200 mb-3 animate-fade-up">
            {bm ? "Sistem Operasi HR untuk Perniagaan Malaysia" : "The HR Operating System for Malaysian Businesses"}
          </Badge>
          <p className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-4 animate-fade-up">
            HR · Payroll · Attendance · Recruitment · Performance · Workforce AI
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight animate-fade-up delay-1">
            {bm ? "Urus HR & Payroll Dalam Satu Platform." : "Run HR & Payroll In One Platform."}
          </h1>
          <p className="mt-5 text-base sm:text-lg text-slate-600 max-w-xl animate-fade-up delay-2">
            {bm
              ? "Daripada attendance dan cuti hingga payroll, tuntutan, kelulusan dan AI — semuanya dalam satu sistem. Patuh KWSP, PERKESO, EIS & PCB 2026."
              : "From attendance and leave to payroll, claims, approvals and AI — all in one system. Compliant with KWSP, PERKESO, EIS & PCB 2026."}
          </p>
          <div className="mt-8 flex flex-wrap gap-3 animate-fade-up delay-3">
            <Button data-testid="landing-hero-cta-demo" size="lg" className="bg-blue-800 hover:bg-blue-900 px-7" onClick={googleLogin}>
              {bm ? "TEMPAH DEMO" : "BOOK DEMO"} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button data-testid="landing-hero-cta-trial" size="lg" variant="outline" className="px-7" onClick={googleLogin}>
              {bm ? "CUBA PERCUMA 14 HARI" : "START FREE TRIAL"}
            </Button>
          </div>
          <div className="mt-8 flex flex-wrap gap-2">
            {["EPF", "SOCSO", "EIS", "PCB 2026", "HRDCorp"].map((s) => (
              <span key={s} className="px-3 py-1 rounded-full border text-xs font-semibold bg-slate-50 text-slate-600">{s}</span>
            ))}
          </div>
        </div>
        <div className="lg:col-span-5 animate-fade-up delay-2">
          <img
            src="https://images.unsplash.com/photo-1758518730083-4c12527b6742?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwzfHxtYWxheXNpYW4lMjBidXNpbmVzcyUyMHByb2Zlc3Npb25hbHMlMjBvZmZpY2UlMjB0ZWFtJTIwaHJ8ZW58MHx8fHwxNzg5Mjk0NjQ0fDA&ixlib=rb-4.1.0&q=85"
            alt="Malaysian HR team"
            className="rounded-2xl shadow-2xl border object-cover aspect-[4/3] w-full"
          />
        </div>
      </section>

      <section className="bg-slate-50 border-y py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
            {bm ? "Satu Pekerja. Satu Enjin. Satu Platform." : "One Employee Profile. One Engine. One Platform."}
          </h2>
          <p className="text-slate-600 mt-1 text-sm font-medium">
            {bm ? "Autonomous Human Resource For Business Technology." : "Autonomous Human Resource For Business Technology."}
          </p>
          <p className="text-slate-600 mt-2 max-w-2xl text-sm sm:text-base">
            {bm
              ? "Satu profil pekerja, satu enjin attendance, satu enjin payroll, satu enjin kelulusan, satu pusat dokumen, satu HR AI."
              : "One employee profile, one attendance engine, one payroll engine, one approval engine, one document centre, one HR AI."}
          </p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {MODULES.map((m, i) => (
              <div key={i} data-testid={`module-card-${i}`} className="bg-white border rounded-xl p-5 hover:shadow-md hover:-translate-y-0.5 transition-all">
                <m.icon className="w-6 h-6 text-blue-800" />
                <p className="mt-3 font-semibold text-sm">{bm ? m.bm : m.en}</p>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-2">
            {INDUSTRIES.map((i) => (
              <span key={i} className="px-3 py-1.5 bg-white border rounded-full text-xs font-medium text-slate-600">{i}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-center">
          {bm ? "Harga Telus. Bayar Mengikut Pekerja Aktif." : "Transparent Pricing. Pay Per Active Employee."}
        </h2>
        <p className="text-center text-slate-600 mt-2 text-sm sm:text-base">
          {bm ? "Yuran asas bulanan + kadar setiap pekerja. Add-on Payroll Outsourcing, AI, Biometric, API & WhatsApp tersedia." : "Monthly base fee + per-employee rate. Payroll Outsourcing, AI, Biometric, API & WhatsApp add-ons available."}
        </p>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map((p) => (
            <div key={p.code} data-testid={`plan-card-${p.code.toLowerCase()}`} className={`border rounded-2xl p-6 bg-white flex flex-col ${p.code === "PROFESSIONAL" ? "ring-2 ring-blue-800 shadow-lg" : ""}`}>
              {p.code === "PROFESSIONAL" && <Badge className="self-start bg-blue-800 mb-3">{bm ? "Paling Popular" : "Most Popular"}</Badge>}
              <h3 className="font-display font-bold text-lg">{p.name}</h3>
              <div className="mt-3">
                {p.base_monthly ? (
                  <>
                    <span className="text-3xl font-extrabold font-mono">RM{p.base_monthly}</span>
                    <span className="text-sm text-slate-500">/{bm ? "bln asas" : "mo base"}</span>
                    <p className="text-sm text-slate-600 mt-1">+ RM{p.price_pepm} / {bm ? "pekerja" : "employee"} / {bm ? "bln" : "mo"}</p>
                  </>
                ) : (
                  <span className="text-2xl font-bold">{bm ? "Harga Custom" : "Custom Pricing"}</span>
                )}
              </div>
              <ul className="mt-4 space-y-2 flex-1">
                {p.modules.slice(0, 5).map((m) => (
                  <li key={m} className="flex gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" /> {m}
                  </li>
                ))}
              </ul>
              <Button className="mt-5 w-full" variant={p.code === "PROFESSIONAL" ? "default" : "outline"} onClick={googleLogin}>
                {bm ? "Mula Sekarang" : "Get Started"}
              </Button>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-900 text-white py-14">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <ShieldCheck className="w-10 h-10 mx-auto text-blue-400" />
          <h2 className="mt-4 text-2xl sm:text-3xl font-bold">
            {bm ? "Selamat, Patuh PDPA & Sentiasa Dikemas Kini" : "Secure, PDPA-Compliant & Always Up To Date"}
          </h2>
          <p className="mt-3 text-slate-300 text-sm sm:text-base">
            {bm
              ? "Pengasingan tenant di peringkat pangkalan data, enjin statutory berversi mengikut tahun taksiran, audit log penuh dan kawalan akses berasaskan peranan."
              : "Database-level tenant isolation, versioned statutory engine per assessment year, full audit logs and role-based access control."}
          </p>
          <Button size="lg" className="mt-7 bg-blue-600 hover:bg-blue-500" onClick={googleLogin}>
            {bm ? "TEMPAH DEMO SEKARANG" : "BOOK YOUR DEMO"} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      <footer className="border-t py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
          <span>© 2026 WORKAHOLIC HR Solution. {bm ? "Semua hak terpelihara." : "All rights reserved."}</span>
          <span>hrplatform.com · abc.hrplatform.com · hr.companyabc.com</span>
        </div>
      </footer>
    </div>
  );
}
